<template>
  <div>
    <div class="header">
      <div class="container-fluid">

        <!-- Alerta de procesos CORE: se muestra solo si el chequeo de salud
             devuelve ERROR (algun proceso CORE lleva demasiado sin reportar). -->
        <div v-if="healthCore && !healthCore.ok" class="alerta-procesos-core mt-3">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <span>{{ $t('revisar_procesos_core') }}</span>
          <ul class="mb-0 mt-2 detalle-core">
            <li v-for="p in procesosCoreConProblema" :key="p.idProceso">
              {{ p.nombre || `IdProceso ${p.idProceso}` }}:
              <template v-if="p.minutosDesdeUltimaEjecucion != null">
                {{ p.minutosDesdeUltimaEjecucion }} {{ $t('minutos') }}
                ({{ $t('tolerancia') }} {{ p.toleranciaMinutos }})
              </template>
              <template v-else>{{ $t('sin_ejecuciones') }}</template>
            </li>
          </ul>
        </div>

        <b-row class="align-items-center mt-3 mb-2">
          <b-col>
            <h5 class="mb-0">{{ $t('estado_procesos') }}</h5>
          </b-col>
          <b-col class="text-right">
            <b-button variant="outline-primary" size="sm" @click="refrescarEstado" :disabled="cargandoEstado">
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': cargandoEstado }"></i> {{ $t('refrescar') }}
            </b-button>
            <b-button variant="outline-secondary" size="sm" class="ml-2" @click="toggleRegistros" :disabled="cargando">
              <i class="fas" :class="registrosVisibles ? 'fa-chevron-up' : 'fa-chevron-down'"></i> {{ $t('ver_detalle') }}
            </b-button>
            <!-- Borrado del historial: acción destructiva reservada a admin, igual
                 que la política "AdminOnly" del endpoint DELETE /api/registrolog. -->
            <b-button v-if="esAdmin" variant="outline-danger" size="sm" class="ml-2" @click="eliminarTodoLog" :disabled="cargandoEstado">
              <i class="fas fa-trash-alt"></i> {{ $t('eliminar_historial') }}
            </b-button>
          </b-col>
        </b-row>

        <b-alert variant="danger" :show="!!errorEstado" dismissible @dismissed="errorEstado = ''">
          {{ errorEstado }}
        </b-alert>

        <div v-if="cargandoEstado" class="text-center text-muted mt-3 mb-4">
          <b-spinner variant="primary" />
        </div>

        <div v-else-if="estadoProcesos.length" class="card shadow mb-4">
          <div class="tabla-scroll-fixed">
            <table class="table table-hover align-items-center mb-0">
              <thead class="thead-sticky thead-light">
                <tr>
                  <th><span class="th-label">{{ $t('proceso') }}</span></th>
                  <th><span class="th-label">{{ $t('observacion') }}</span></th>
                  <th><span class="th-label">{{ $t('ultima_ejecucion') }}</span></th>
                  <th class="text-center"><span class="th-label">{{ $t('minutos') }}</span></th>
                  <th><span class="th-label">{{ $t('acciones') }}</span></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="item in estadoProcesos">
                  <tr :key="item.idProceso" :class="{ 'fila-atrasada': esAtrasado(item) }">
                    <td>{{ item.nombre || '-' }}</td>
                    <td>{{ item.observacion || '-' }}</td>
                    <td>
                      <span v-if="item.ultimaEjecucion">{{ formatFecha(item.ultimaEjecucion) }}</span>
                      <span v-else class="badge badge-danger">{{ $t('sin_ejecuciones') }}</span>
                    </td>
                    <td class="text-center">
                      <span v-if="item.minutosDesdeUltimaEjecucion !== null && item.minutosDesdeUltimaEjecucion !== undefined">{{ item.minutosDesdeUltimaEjecucion }}</span>
                      <span v-else>-</span>
                    </td>
                    <td>
                      <b-button variant="outline-secondary" size="sm" @click="toggleDetalle(item)">
                        <i class="fas" :class="detalleAbierto[item.idProceso] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        {{ $t('ver_detalle') }}
                      </b-button>
                    </td>
                  </tr>
                  <tr v-if="detalleAbierto[item.idProceso]" :key="`${item.idProceso}-detalle`">
                    <td colspan="5" class="p-0">
                      <div class="p-3" style="background-color: #f8f9fa;">
                        <div v-if="detalleCargando[item.idProceso]" class="text-center text-muted">
                          <b-spinner variant="primary" small />
                        </div>
                        <b-alert v-else-if="detalleError[item.idProceso]" variant="danger" show>
                          {{ detalleError[item.idProceso] }}
                        </b-alert>
                        <div v-else-if="(detalleRegistros[item.idProceso] || []).length">
                          <table class="table table-sm table-borderless mb-0">
                            <thead>
                              <tr>
                                <th><span class="th-label">{{ $t('fecha') }}</span></th>
                                <th><span class="th-label">{{ $t('estado') }}</span></th>
                                <th><span class="th-label">{{ $t('columna_error') }}</span></th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(det, dIdx) in detalleRegistros[item.idProceso]" :key="dIdx">
                                <td>{{ formatFecha(det.fecha) }}</td>
                                <td>
                                  <span class="badge" :class="det.estado ? 'badge-success' : 'badge-danger'">
                                    {{ det.estado ? $t('estado_ok') : $t('columna_error') }}
                                  </span>
                                </td>
                                <td>{{ det.error || '-' }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="text-muted mb-0">{{ $t('sin_ejecuciones_registradas') }}</div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <div v-else-if="!cargandoEstado" class="text-center text-muted mt-3 mb-4">
          <p>{{ $t('no_hay_procesos_registrados') }}</p>
        </div>

        <template v-if="registrosVisibles">

        <hr class="mb-4">

        <b-row class="align-items-center mt-3 mb-2">
          <b-col>
            <span class="text-muted" v-if="registrosFiltrados.length !== registros.length">
              {{ registrosFiltrados.length }} / {{ registros.length }} {{ $t('registros') }}
            </span>
            <span class="text-muted" v-else>{{ registros.length }} {{ $t('registros') }}</span>
          </b-col>
          <b-col class="text-right">
            <b-button variant="outline-primary" size="sm" @click="cargarRegistros" :disabled="cargando">
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': cargando }"></i> {{ $t('refrescar') }}
            </b-button>
          </b-col>
        </b-row>

        <b-alert variant="danger" :show="!!error" dismissible @dismissed="error = ''">
          {{ error }}
        </b-alert>

        <div v-if="cargando" class="text-center text-muted mt-5">
          <b-spinner variant="primary" />
        </div>

        <div v-else-if="registros.length" class="card shadow" style="margin-bottom: 60px;">
          <div class="tabla-scroll-fixed" ref="tablaRegistros">
            <table class="table table-hover align-items-center mb-0">
              <thead class="thead-sticky thead-light">
                <tr>
                  <th class="th-ordenable" @click="ordenarPor('fecha')">
                    <span class="th-label">{{ $t('fecha') }} <i :class="iconoOrden('fecha')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('nombre')">
                    <input class="th-search-input" v-model="filterCol.nombre" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('proceso') }} <i :class="iconoOrden('nombre')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('estado')">
                    <span class="th-label">{{ $t('estado') }} <i :class="iconoOrden('estado')"></i></span>
                  </th>
                  <th>
                    <span class="th-label">{{ $t('columna_error') }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in registrosPaginados" :key="idx">
                  <td>{{ formatFecha(item.fecha) }}</td>
                  <td>{{ item.nombre || '-' }}</td>
                  <td>
                    <span class="badge" :class="item.estado ? 'badge-success' : 'badge-danger'">
                      {{ item.estado ? $t('estado_ok') : $t('columna_error') }}
                    </span>
                  </td>
                  <td>{{ item.error || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="pagination-floating-bar" v-if="registros.length">
          <div class="d-flex align-items-center">
            <label class="mr-2 mb-0 text-secondary" style="font-size:.85rem;">{{ $t('items_por_pagina') }}</label>
            <select v-model.number="pageSize" @change="currentPage = 1" class="form-control form-control-sm" style="width:70px;">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="999999">{{ $t('todas') }}</option>
            </select>
          </div>
          <div class="d-flex align-items-center" style="gap:12px;">
            <button class="btn btn-outline-secondary btn-sm" :disabled="currentPage === 1" @click="currentPage--">
              <i class="fas fa-arrow-left mr-1"></i> {{ $t('anterior') }}
            </button>
            <span style="font-weight:600;color:#6c757d;font-size:.9rem;">
              {{ $t('pagina') }} {{ currentPage }} {{ $t('de') }} {{ totalPages }}
              <span class="text-muted ml-2" style="font-size:.8rem;">({{ registrosFiltrados.length }} {{ $t('resultados') }})</span>
            </span>
            <button class="btn btn-outline-primary btn-sm" :disabled="currentPage >= totalPages" @click="currentPage++">
              {{ $t('siguiente') }} <i class="fas fa-arrow-right ml-1"></i>
            </button>
          </div>
        </div>

        <div v-else-if="!cargando" class="text-center text-muted mt-5">
          <i class="fas fa-list-alt fa-3x mb-3"></i>
          <p>{{ $t('no_hay_registros_de_log') }}</p>
        </div>

        </template>

        <!-- Archivos cargados: vista transversal de dbo.ArchivoLogs (todas las empresas),
             sin el flujo VM. Va antes del panel de funciones. -->
        <b-row class="align-items-center mt-4 mb-2">
          <b-col>
            <h5 class="mb-0">
              {{ $t('archivos_cargados') }}
              <span class="text-muted" style="font-size:.8rem;">
                ({{ archivosCargadosFiltrados.length }} {{ $t('registros').toLowerCase() }})
              </span>
            </h5>
          </b-col>
          <b-col class="text-right">
            <b-form-checkbox v-model="soloActivosArch" switch inline class="mr-3 align-middle"
              :disabled="cargandoArchivos" @change="onSoloActivosArchChange">
              {{ $t('solo_activos') }}
            </b-form-checkbox>
            <b-button variant="outline-secondary" size="sm" @click="limpiarFiltrosArch">
              <i class="fas fa-eraser"></i> {{ $t('limpiar_filtros') }}
            </b-button>
            <b-button variant="outline-primary" size="sm" class="ml-2"
              @click="cargarArchivosCargados" :disabled="cargandoArchivos">
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': cargandoArchivos }"></i> {{ $t('refrescar') }}
            </b-button>
          </b-col>
        </b-row>

        <b-alert variant="danger" :show="!!errorArchivos" dismissible @dismissed="errorArchivos = ''">
          {{ errorArchivos }}
        </b-alert>

        <div v-if="cargandoArchivos" class="text-center text-muted mt-3 mb-4">
          <b-spinner variant="primary" />
        </div>

        <div v-else-if="archivosCargadosFiltrados.length" class="card shadow mb-4">
          <div class="tabla-scroll-fixed tabla-archivos-scroll">
            <table class="table table-hover align-items-center mb-0">
              <thead class="thead-sticky thead-light">
                <tr>
                  <th class="th-ordenable" style="min-width:150px;" @click="ordenarPorArch('usuario')">
                    <input class="th-search-input" v-model="filterColArch.usuario" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('cargado_por') }} <i :class="iconoOrdenArch('usuario')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:180px;" @click="ordenarPorArch('nombre')">
                    <input class="th-search-input" v-model="filterColArch.nombre" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('empresa') }} <i :class="iconoOrdenArch('nombre')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:150px;" @click="ordenarPorArch('createdAt')">
                    <input class="th-search-input" v-model="filterColArch.createdAt" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('fecha_carga') }} (GMT 0) <i :class="iconoOrdenArch('createdAt')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:130px;" @click="ordenarPorArch('estadoFlujoDesc')">
                    <input class="th-search-input" v-model="filterColArch.estadoFlujoDesc" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('estado') }} <i :class="iconoOrdenArch('estadoFlujoDesc')"></i></span>
                  </th>
                  <th class="th-ordenable text-center" style="min-width:130px;" @click="ordenarPorArch('tipoFlujo')">
                    <input class="th-search-input" v-model="filterColArch.tipoFlujo" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('tipo_flujo') }} <i :class="iconoOrdenArch('tipoFlujo')"></i></span>
                  </th>
                  <th class="th-ordenable text-center" style="min-width:80px;" @click="ordenarPorArch('activo')">
                    <input class="th-search-input" v-model="filterColArch.activo" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('activo') }} <i :class="iconoOrdenArch('activo')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:230px;" @click="ordenarPorArch('token')">
                    <input class="th-search-input" v-model="filterColArch.token" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('token') }} <i :class="iconoOrdenArch('token')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:200px;" @click="ordenarPorArch('nombreXLSX')">
                    <input class="th-search-input" v-model="filterColArch.nombreXLSX" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('nombre_archivo') }} <i :class="iconoOrdenArch('nombreXLSX')"></i></span>
                  </th>
                  <th class="th-ordenable text-right" style="min-width:90px;" @click="ordenarPorArch('pesoKb')">
                    <input class="th-search-input" v-model="filterColArch.pesoKb" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('tamano') }} <i :class="iconoOrdenArch('pesoKb')"></i></span>
                  </th>
                  <th class="th-ordenable text-right" style="min-width:90px;" @click="ordenarPorArch('idEmpresa')">
                    <input class="th-search-input" v-model="filterColArch.idEmpresa" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('id_empresa') }} <i :class="iconoOrdenArch('idEmpresa')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:150px;" @click="ordenarPorArch('updatedAt')">
                    <input class="th-search-input" v-model="filterColArch.updatedAt" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('fecha_cambio_estado') }} <i :class="iconoOrdenArch('updatedAt')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:180px;" @click="ordenarPorArch('mensajeError')">
                    <input class="th-search-input" v-model="filterColArch.mensajeError" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('columna_error') }} <i :class="iconoOrdenArch('mensajeError')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:160px;" @click="ordenarPorArch('fechaRentabilizacion')">
                    <input class="th-search-input" v-model="filterColArch.fechaRentabilizacion" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('fecha_rentabilizacion') }} (GMT 0) <i :class="iconoOrdenArch('fechaRentabilizacion')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:160px;" @click="ordenarPorArch('fechaClusterizacion')">
                    <input class="th-search-input" v-model="filterColArch.fechaClusterizacion" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('fecha_clusterizacion') }} (GMT 0) <i :class="iconoOrdenArch('fechaClusterizacion')"></i></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in archivosCargadosFiltrados" :key="item.token">
                  <td>{{ item.usuario || '-' }}</td>
                  <td>{{ item.nombre || '-' }}</td>
                  <td>{{ formatFecha(item.createdAt) }}</td>
                  <td>{{ item.estadoFlujoDesc || '-' }}</td>
                  <td class="text-center">
                    <span class="badge" :class="tipoFlujoVariante(item)">{{ tipoFlujoLabel(item) }}</span>
                  </td>
                  <td class="text-center">
                    <span class="badge" :class="item.activo ? 'badge-success' : 'badge-secondary'">
                      {{ item.activo ? $t('si') : $t('no') }}
                    </span>
                  </td>
                  <td class="celda-token text-monospace" :title="item.token">{{ item.token || '-' }}</td>
                  <td>{{ item.nombreXLSX || '-' }}</td>
                  <td class="text-right">{{ item.pesoKb }} KB</td>
                  <td class="text-right">{{ item.idEmpresa }}</td>
                  <td>{{ formatFecha(item.updatedAt) }}</td>
                  <td class="celda-error" :title="item.mensajeError || ''">{{ item.mensajeError || '-' }}</td>
                  <td>{{ formatFecha(item.fechaRentabilizacion) }}</td>
                  <td>{{ formatFecha(item.fechaClusterizacion) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-else-if="!cargandoArchivos" class="text-center text-muted mb-4">
          {{ $t('sin_archivos_empresa') }}
        </div>

        <!-- Panel de Funciones y Control: venía de dashboard-master. Reservado a
             admin, igual que allá. El margen inferior evita que la barra flotante
             de paginación lo tape cuando el detalle de registros está abierto.
             Oculto por ahora: para volver a mostrarlo, poner mostrarPanelFunciones en true. -->
        <div v-if="esAdmin && mostrarPanelFunciones" class="chart-card chart-card--featured panel-funciones">
          <div class="chart-header">
            <div>
              <div class="chart-title chart-title--lg">{{ $t('dm_panel_title') }}</div>
              <div class="chart-subtitle">{{ $t('dm_panel_sub') }}</div>
            </div>
            <span class="chart-badge">{{ $t('dm_panel_badge') }}</span>
          </div>
          <div style="overflow-x:auto">
            <table class="ranking-table" style="min-width:700px">
              <thead>
                <tr>
                  <th style="width:220px">{{ $t('dm_th_funcion') }}</th>
                  <th>{{ $t('dm_th_descripcion') }}</th>
                  <th class="rt-col-num" style="width:90px">{{ $t('dm_th_estado') }}</th>
                  <th class="rt-col-num" style="width:140px">{{ $t('dm_th_ejecucion') }}</th>
                  <th class="rt-col-num" style="width:130px">{{ $t('dm_th_acciones') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="fn in plataformaFunciones" :key="fn.key">
                  <td class="rt-nombre">{{ $t(fn.nombre) }}</td>
                  <td style="font-size:0.82rem;color:#6b7280;padding:12px">{{ $t(fn.descripcion) }}</td>
                  <td class="rt-conf" style="text-align:center">
                    <span class="fn-badge fn-badge--activo">{{ $t('dm_fn_activo') }}</span>
                  </td>
                  <td style="font-size:0.82rem;text-align:right;padding:12px;color:#6b7280">{{ $t(fn.ultimaEjecucion) }}</td>
                  <td style="text-align:right;padding:12px">
                    <button class="fn-btn">{{ $t(fn.accion) }}</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import registroLogService from '@/api/registroLogService';
import materialService from '@/api/materialService';
import utilsService from '@/api/utilsService.vue';
import Swal from 'sweetalert2';

export default {
  name: 'LogStatusView',
  data() {
    return {
      registros: [],
      registrosVisibles: false,
      cargando: false,
      error: '',
      ordenColumna: 'fecha',
      ordenDireccion: 'desc',
      filterCol: {
        nombre: '',
      },
      currentPage: 1,
      pageSize: 50,

      estadoProcesos: [],
      cargandoEstado: false,
      errorEstado: '',

      // Archivos cargados (todas las empresas). Filtros por columna y ordenación
      // client-side, con el mismo patrón que la tabla de /inventario. Los nombres
      // llevan sufijo "Arch" para no pisar los de la tabla de registros de log.
      archivosCargados: [],
      cargandoArchivos: false,
      errorArchivos: '',
      // Las cargas dadas de baja son ruido en el seguimiento diario: se piden aparte.
      soloActivosArch: true,
      filterColArch: {
        usuario: '',
        nombre: '',
        createdAt: '',
        estadoFlujoDesc: '',
        activo: '',
        token: '',
        nombreXLSX: '',
        tipoFlujo: '',
        pesoKb: '',
        idEmpresa: '',
        updatedAt: '',
        mensajeError: '',
        fechaRentabilizacion: '',
        fechaClusterizacion: '',
      },
      // Arranca igual que el ORDER BY del servicio: fecha de carga descendente.
      ordenColumnaArch: 'createdAt',
      ordenDireccionArch: 'desc',

      // null mientras no se sabe: la alerta no se pinta hasta tener respuesta,
      // para no mostrar una falsa alarma durante la carga.
      healthCore: null,

      detalleAbierto: {},
      detalleCargando: {},
      detalleError: {},
      detalleRegistros: {},

      // El panel de funciones queda oculto; se conserva el markup para poder
      // reactivarlo con solo cambiar esta bandera.
      mostrarPanelFunciones: false,

      // Filas del panel de funciones. Se guardan las claves de traducción, no el
      // texto: $t en el template se reevalúa en cada render, mientras que un
      // computed que llame a $t no reacciona al cambio de idioma (el traductor de
      // @/util/language.js lee de localStorage y no es reactivo).
      plataformaFunciones: [
        { key: 'etl',       nombre: 'dm_fn_etl_nombre',       descripcion: 'dm_fn_etl_desc',       ultimaEjecucion: 'dm_fn_etl_ejec',       accion: 'dm_fn_etl_accion' },
        { key: 'ia',        nombre: 'dm_fn_ia_nombre',        descripcion: 'dm_fn_ia_desc',        ultimaEjecucion: 'dm_fn_ia_ejec',        accion: 'dm_fn_ia_accion' },
        { key: 'precios',   nombre: 'dm_fn_precios_nombre',   descripcion: 'dm_fn_precios_desc',   ultimaEjecucion: 'dm_fn_precios_ejec',   accion: 'dm_fn_precios_accion' },
        { key: 'rfm',       nombre: 'dm_fn_rfm_nombre',       descripcion: 'dm_fn_rfm_desc',       ultimaEjecucion: 'dm_fn_rfm_ejec',       accion: 'dm_fn_rfm_accion' },
        { key: 'auditoria', nombre: 'dm_fn_auditoria_nombre', descripcion: 'dm_fn_auditoria_desc', ultimaEjecucion: 'dm_fn_auditoria_ejec', accion: 'dm_fn_auditoria_accion' },
      ],
    };
  },

  watch: {
    filterCol: { deep: true, handler() { this.currentPage = 1; } },
  },

  mounted() {
    this.$store.dispatch('setSidebarCollapsed', false);
    this.cargarEstadoProcesos();
    this.cargarHealthCore();
    this.cargarArchivosCargados();
  },

  beforeDestroy() {
    this.$store.dispatch('setSidebarCollapsed', false);
  },

  computed: {
    esAdmin() {
      return utilsService.getRol() === 'admin';
    },

    // Filtros por columna y ordenación de la tabla de archivos cargados.
    // El filtro compara contra el texto que se ve en pantalla (fecha formateada,
    // tipo de flujo traducido, Sí/No), que es lo que el usuario espera escribir.
    archivosCargadosFiltrados() {
      let rows = Array.isArray(this.archivosCargados) ? this.archivosCargados.slice() : [];

      const f = this.filterColArch;
      rows = rows.filter(r => {
        for (const k in f) {
          if (!Object.prototype.hasOwnProperty.call(f, k)) continue;
          const needle = String(f[k] == null ? '' : f[k]).trim().toLowerCase();
          if (!needle) continue;
          const cell = String(this.valorVisibleArch(r, k) || '').toLowerCase();
          if (cell.indexOf(needle) === -1) return false;
        }
        return true;
      });

      if (this.ordenColumnaArch) {
        const key = this.ordenColumnaArch;
        const dir = this.ordenDireccionArch === 'asc' ? 1 : -1;
        const esFecha = ['createdAt', 'updatedAt', 'fechaRentabilizacion', 'fechaClusterizacion'].includes(key);
        rows.sort((a, b) => {
          const va = a && a[key] != null ? a[key] : '';
          const vb = b && b[key] != null ? b[key] : '';
          // Las fechas se ordenan por su valor real, no por el texto formateado.
          if (esFecha) return ((va ? Date.parse(va) : 0) - (vb ? Date.parse(vb) : 0)) * dir;
          const na = parseFloat(va); const nb = parseFloat(vb);
          if (!isNaN(na) && !isNaN(nb)) return (na - nb) * dir;
          return String(va).localeCompare(String(vb)) * dir;
        });
      }

      return rows;
    },

    procesosCoreConProblema() {
      if (!this.healthCore || !this.healthCore.procesos) return [];
      return this.healthCore.procesos.filter(p => !p.ok);
    },

    registrosFiltrados() {
      const fc = this.filterCol;
      let lista = this.registros;
      if (fc.nombre) lista = lista.filter(r => (r.nombre || '').toLowerCase().includes(fc.nombre.toLowerCase()));
      return lista;
    },
    registrosOrdenados() {
      if (!this.ordenColumna) return this.registrosFiltrados;
      const sorted = [...this.registrosFiltrados];
      sorted.sort((a, b) => {
        let vA = a[this.ordenColumna];
        let vB = b[this.ordenColumna];
        if (vA == null) vA = '';
        if (vB == null) vB = '';
        if (this.ordenColumna === 'fecha') {
          vA = new Date(vA).getTime();
          vB = new Date(vB).getTime();
          return this.ordenDireccion === 'asc' ? vA - vB : vB - vA;
        }
        if (typeof vA === 'boolean' && typeof vB === 'boolean') {
          return this.ordenDireccion === 'asc' ? (vA === vB ? 0 : vA ? 1 : -1) : (vA === vB ? 0 : vA ? -1 : 1);
        }
        vA = String(vA).toLowerCase();
        vB = String(vB).toLowerCase();
        if (vA < vB) return this.ordenDireccion === 'asc' ? -1 : 1;
        if (vA > vB) return this.ordenDireccion === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    },
    totalPages() {
      if (this.pageSize >= 999999) return 1;
      return Math.max(1, Math.ceil(this.registrosOrdenados.length / this.pageSize));
    },
    registrosPaginados() {
      if (this.pageSize >= 999999) return this.registrosOrdenados;
      const start = (this.currentPage - 1) * this.pageSize;
      return this.registrosOrdenados.slice(start, start + this.pageSize);
    },
  },

  methods: {
    toggleRegistros() {
      if (this.registrosVisibles) {
        this.registrosVisibles = false;
        return;
      }
      this.registrosVisibles = true;
      this.cargarRegistros();
    },

    async cargarRegistros() {
      this.cargando = true;
      this.error = '';
      try {
        const data = await registroLogService.getAll();
        this.registros = (data || []).map(r => ({
          fecha: r.fecha,
          estado: r.estado,
          error: r.error,
          nombre: r.nombre,
        }));
      } catch (e) {
        this.error = (e && e.response && e.response.data && e.response.data.message) || 'Ocurrió un error al cargar los registros de log.';
      } finally {
        this.cargando = false;
      }
    },

    async cargarEstadoProcesos() {
      this.cargandoEstado = true;
      this.errorEstado = '';
      try {
        const data = await registroLogService.getEstadoProcesos();
        this.estadoProcesos = (data || []).map(r => ({
          idProceso: r.idProceso,
          nombre: r.nombre,
          observacion: r.observacion,
          ultimaEjecucion: r.ultimaEjecucion,
          minutosDesdeUltimaEjecucion: r.minutosDesdeUltimaEjecucion,
        }));
      } catch (e) {
        this.errorEstado = (e && e.response && e.response.data && e.response.data.message) || 'Ocurrió un error al cargar el estado de procesos.';
      } finally {
        this.cargandoEstado = false;
      }
    },

    // El boton "Actualizar" refresca tambien el chequeo CORE: si no, la alerta
    // se quedaria pegada con el resultado del mounted.
    refrescarEstado() {
      this.cargarEstadoProcesos();
      this.cargarHealthCore();
    },

    async cargarHealthCore() {
      try {
        this.healthCore = await registroLogService.getHealthProcesosCore();
      } catch (e) {
        // Si el chequeo no responde no se pinta la alerta: no hay informacion
        // para afirmar que los procesos CORE esten caidos, y la vista tiene que
        // seguir sirviendo aunque este endpoint falle.
        this.healthCore = null;
      }
    },

    eliminarTodoLog() {
      if (!this.esAdmin) return;
      Swal.fire({
        title: this.$t('esta_seguro'),
        text: this.$t('eliminar_historial_log_permanente'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('eliminar'),
        cancelButtonText: this.$t('cancelar'),
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await registroLogService.eliminarTodo();
            this.detalleAbierto = {};
            this.detalleRegistros = {};
            this.detalleError = {};
            await this.cargarEstadoProcesos();
            if (this.registrosVisibles) {
              await this.cargarRegistros();
            }
            Swal.fire({ text: this.$t('eliminado_correctamente'), icon: 'success', timer: 2000, showConfirmButton: false });
          } catch (e) {
            this.errorEstado = this.$t('error_eliminar_historial_log');
          }
        }
      });
    },

    async toggleDetalle(item) {
      const idProceso = item.idProceso;

      if (this.detalleAbierto[idProceso]) {
        this.$set(this.detalleAbierto, idProceso, false);
        return;
      }

      this.$set(this.detalleAbierto, idProceso, true);

      if (this.detalleRegistros[idProceso]) {
        return;
      }

      this.$set(this.detalleCargando, idProceso, true);
      this.$set(this.detalleError, idProceso, '');

      try {
        const data = await registroLogService.getByProceso(idProceso);
        this.$set(this.detalleRegistros, idProceso, (data || []).map(r => ({
          fecha: r.fecha,
          estado: r.estado,
          error: r.error,
        })));
      } catch (e) {
        this.$set(this.detalleError, idProceso, (e && e.response && e.response.data && e.response.data.message) || 'Ocurrió un error al cargar el detalle del proceso.');
      } finally {
        this.$set(this.detalleCargando, idProceso, false);
      }
    },

    esAtrasado(item) {
      return item.minutosDesdeUltimaEjecucion != null && item.minutosDesdeUltimaEjecucion > 60;
    },

    formatFecha(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      return d.toLocaleString();
    },

    // Se usa el valor que emite el interruptor y no this.soloActivosArch: asi la recarga
    // no depende de si el v-model ya se actualizo cuando llega el evento.
    onSoloActivosArchChange(valor) {
      this.soloActivosArch = valor;
      return this.cargarArchivosCargados();
    },

    async cargarArchivosCargados() {
      this.cargandoArchivos = true;
      this.errorArchivos = '';
      try {
        this.archivosCargados = await materialService.getArchivosCargados(this.soloActivosArch);
      } catch (e) {
        console.error('[cargarArchivosCargados] error:', e);
        this.errorArchivos = this.$t('error_cargar_archivos') || 'No se pudieron cargar los archivos.';
        this.archivosCargados = [];
      } finally {
        this.cargandoArchivos = false;
      }
    },

    ordenarPorArch(columna) {
      if (this.ordenColumnaArch === columna) {
        this.ordenDireccionArch = this.ordenDireccionArch === 'asc' ? 'desc' : 'asc';
      } else {
        this.ordenColumnaArch = columna;
        this.ordenDireccionArch = 'asc';
      }
    },

    iconoOrdenArch(columna) {
      if (this.ordenColumnaArch !== columna) return 'fas fa-sort text-muted';
      return this.ordenDireccionArch === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },

    limpiarFiltrosArch() {
      for (const k in this.filterColArch) {
        this.$set(this.filterColArch, k, '');
      }
    },

    // Texto tal como se ve en la celda, para que el filtro escrito coincida con lo
    // que el usuario tiene delante y no con el dato crudo.
    valorVisibleArch(row, key) {
      if (!row) return '';
      if (key === 'tipoFlujo') return this.tipoFlujoLabel(row);
      if (key === 'activo') return row.activo ? this.$t('si') : this.$t('no');
      if (['createdAt', 'updatedAt', 'fechaRentabilizacion', 'fechaClusterizacion'].includes(key)) {
        return this.formatFecha(row[key]);
      }
      return row[key];
    },

    // Etiqueta traducida del tipo de flujo. Se prefiere el código (FN/NR/DE) sobre la
    // descripción del diccionario, que solo viene en español.
    tipoFlujoLabel(item) {
      const codigo = String((item && item.tipoFlujoCodigo) || '').trim().toUpperCase();
      const claves = { FN: 'tipo_flujo_fn', NR: 'tipo_flujo_nr', DE: 'tipo_flujo_de' };
      if (claves[codigo]) return this.$t(claves[codigo]);
      return (item && item.tipoFlujo) || '—';
    },

    tipoFlujoVariante(item) {
      const codigo = String((item && item.tipoFlujoCodigo) || '').trim().toUpperCase();
      if (codigo === 'FN') return 'badge-primary';
      if (codigo === 'NR') return 'badge-warning';
      if (codigo === 'DE') return 'badge-info';
      return 'badge-secondary';
    },

    ordenarPor(columna) {
      if (this.ordenColumna === columna) {
        this.ordenDireccion = this.ordenDireccion === 'asc' ? 'desc' : 'asc';
      } else {
        this.ordenColumna = columna;
        this.ordenDireccion = 'asc';
      }
      this.currentPage = 1;
    },

    iconoOrden(columna) {
      if (this.ordenColumna !== columna) return 'fas fa-sort text-muted';
      return this.ordenDireccion === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 10px;
  overflow: hidden;
}

.alerta-procesos-core {
  background-color: #dc3545;
  color: #fff;
  border-radius: 8px;
  padding: 14px 18px;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.35);
}

.alerta-procesos-core .detalle-core {
  font-weight: 400;
  font-size: 0.85rem;
  padding-left: 26px;
}

.fila-atrasada td {
  background-color: rgba(220, 53, 69, 0.15) !important;
}

.tabla-scroll-fixed {
  overflow-x: auto;
  position: relative;
  font-size: 0.8rem;
}

.thead-sticky {
  position: relative;
  z-index: 2;
  background: #f8f9fa;
}

.th-ordenable {
  cursor: pointer;
  user-select: none;
  min-width: 90px;
  vertical-align: top;
  white-space: nowrap;
}
.th-ordenable:hover {
  background-color: #e9ecef !important;
}

.th-search-input {
  width: 100%;
  font-size: 11px;
  padding: 2px 4px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  margin-bottom: 3px;
  display: block;
  font-weight: normal;
}

.th-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.tabla-scroll-fixed .table th,
.tabla-scroll-fixed .table td {
  font-size: 12px;
}

/* Mensaje de error de la carga: puede ser un stack trace completo, así que se
   acota el ancho y se muestra íntegro en el tooltip. */
.celda-error {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Tabla de archivos cargados: alto acotado con scroll propio, para que no empuje
   hacia abajo el panel de funciones por más filas que traiga. */
.tabla-archivos-scroll {
  max-height: 600px;
  overflow-y: auto;
}
/* Dentro de un contenedor con scroll el encabezado tiene que ir sticky sobre las
   celdas (el .thead-sticky general de la vista es solo relative). */
.tabla-archivos-scroll thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f8f9fa;
}

/* El token es un GUID de 36 caracteres: se muestra completo pero en menor tamaño
   para que no domine el ancho de la tabla. */
.celda-token {
  font-size: 10px;
  white-space: nowrap;
  color: #6b7280;
}

/* ── Panel de Funciones y Control ──────────────────────────────────────
   Estilos traídos desde DashboardMaster junto con la sección; su <style> es
   scoped, así que no se heredaban. */
.panel-funciones {
  margin-top: 24px;
  /* Deja aire para la barra flotante de paginación, que es position:fixed. */
  margin-bottom: 80px;
}
.chart-card {
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e8ecf0;
  padding: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.chart-card:hover {
  border-color: #00A3E0;
  box-shadow: 0 0 0 2px rgba(0,163,224,.12), 0 2px 8px rgba(0,0,0,.06);
}
.chart-card--featured { background: #fff; }
.chart-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
}
.chart-title--lg {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0;
}
.chart-subtitle {
  font-size: 0.78rem;
  color: #6b7280;
  margin-top: 3px;
}
.chart-badge {
  flex-shrink: 0;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.72rem;
  color: #374151;
  font-family: monospace;
  white-space: nowrap;
}
.ranking-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.ranking-table thead tr { border-bottom: 2px solid #f3f4f6; }
.ranking-table thead th {
  padding: 8px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  text-align: left;
}
.ranking-table tbody tr {
  border-bottom: 1px solid #f9fafb;
  transition: background 0.15s;
}
.ranking-table tbody tr:hover { background: #f9fafb; }
.rt-col-num { text-align: right !important; }
.rt-nombre {
  padding: 13px 12px;
  font-weight: 600;
  color: #111827;
}
.rt-conf {
  padding: 13px 12px;
  text-align: right;
  font-weight: 700;
  color: #00A3E0;
}
.fn-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 700;
}
.fn-badge--activo { background: #d1fae5; color: #065f46; }
.fn-btn {
  background: #00A3E0;
  color: #fff;
  border: none;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.fn-btn:hover { background: #0081b2; }

.pagination-floating-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  background: #fff;
  border-top: 1px solid #dee2e6;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}
</style>
